import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { StatusBadge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { FormGroup } from '@/components/form/FormGroup';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import { Download, FileDown, Search as SearchIcon, Trash2, Upload } from '@/components/icons';
import {
  listPriceMaster, upsertPriceMaster, deletePriceMaster, deleteAllPriceMaster, importPriceMaster,
  priceMasterExportPath, priceMasterImportTemplatePath, CUSTOMER_TYPES,
  type PriceMasterEntry, type UpsertPriceMasterPayload,
} from '@/api/price-master';

function fmt(val: string | null | undefined) {
  if (!val) return '—';
  return parseFloat(val).toFixed(2) + ' %';
}

const emptyForm = (): UpsertPriceMasterPayload => ({
  priceList: CUSTOMER_TYPES[0],
  discount: null,
  specialDiscount: null,
  status: 'Active',
  remarks: null,
});

export function PriceMasterPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<UpsertPriceMasterPayload>(emptyForm());
  const [editingId, setEditingId] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['price-master', statusFilter],
    queryFn: () => listPriceMaster({ status: statusFilter || undefined }),
  });

  const saveMut = useMutation({
    mutationFn: (p: UpsertPriceMasterPayload) => upsertPriceMaster(p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['price-master'] });
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      toast.push('Saved successfully', 'success');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deletePriceMaster(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['price-master'] });
      toast.push('Entry deleted', 'success');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Delete failed'), 'error');
    },
  });

  const delAllMut = useMutation({
    mutationFn: deleteAllPriceMaster,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['price-master'] });
      toast.push(`Deleted ${res.deleted} entries`, 'info');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Delete failed'), 'error');
    },
  });

  async function onDeleteAll() {
    if (!await confirmDlg(`Delete ALL price master entries? This cannot be undone.`)) return;
    delAllMut.mutate();
  }

  const importMut = useMutation({
    mutationFn: (file: File) => importPriceMaster(file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['price-master'] });
      toast.push(`Imported ${res.imported} rows${res.skipped ? `, skipped ${res.skipped}` : ''}`, 'success');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Import failed'), 'error');
    },
  });

  function openNew() {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(row: PriceMasterEntry) {
    setForm({
      priceList:       row.priceList,
      discount:        row.discount        != null ? parseFloat(row.discount)        : null,
      specialDiscount: row.specialDiscount != null ? parseFloat(row.specialDiscount) : null,
      status:          row.status,
      remarks:         row.remarks ?? null,
    });
    setEditingId(row.id);
    setShowForm(true);
  }

  function handleImport(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) importMut.mutate(file);
    e.target.value = '';
  }

  const rows = query.data?.data ?? [];

  return (
    <div>
      <SectionHeader
        title="Price Master"      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <SearchIcon
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[240px]"
              aria-label="Search price entries"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="sm:min-w-[140px]"
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </Select>
        </FilterField>
        {/* Merged chip strip: visible on both viewports, right-aligned inside the filter block. */}
        <div className="col-span-3 flex flex-nowrap gap-1.5 [&>*]:flex-auto [&>*]:whitespace-nowrap sm:col-span-1 sm:ml-auto sm:items-end sm:self-end sm:[&>*]:flex-none">
          <button
            type="button"
            onClick={() => downloadExcel(priceMasterExportPath({ status: statusFilter || undefined }), 'GRHAMOY_PriceMaster.xlsx')}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-success hover:text-success sm:h-8 sm:px-3 sm:text-xs"
          >
            <Download size={12} /> Export
          </button>
          <button
            type="button"
            onClick={() => downloadExcel(priceMasterImportTemplatePath(), 'GRHAMOY_PriceMaster_Template.xlsx')}
            title="Download a sample Excel with the correct columns and one example row"
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            <FileDown size={12} /> Template
          </button>
          <label className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs">
            <Upload size={12} /> Import
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          </label>
          <button
            type="button"
            onClick={onDeleteAll}
            disabled={delAllMut.isPending || rows.length === 0}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-danger shadow-sm transition hover:border-danger hover:text-danger disabled:opacity-50 sm:h-8 sm:px-3 sm:text-xs"
          >
            {delAllMut.isPending ? (
              'Deleting…'
            ) : (
              <span className="inline-flex items-center gap-1"><Trash2 size={12} /> Delete All</span>
            )}
          </button>
          <button
            type="button"
            onClick={openNew}
            className="inline-flex h-7 items-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:h-8 sm:px-3 sm:text-xs"
          >
            ＋ Add Entry
          </button>
        </div>
      </FilterBar>

      <Table<PriceMasterEntry>
        storageKey="price-master"
        title="Price Master"
        count={`${rows.length} customer type${rows.length !== 1 ? 's' : ''}`}
        rows={rows}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        empty="No entries yet. Add one per customer type."
        columns={[
          {
            key: 'priceList',
            header: 'Customer Type',
            cell: (r) => <span className="font-bold">{r.priceList}</span>,
          },
          {
            key: 'discount',
            header: 'Discount %',
            align: 'right',
            cell: (r) => <span className="font-mono text-accent">{fmt(r.discount)}</span>,
          },
          {
            key: 'specialDiscount',
            header: 'Special Discount %',
            align: 'right',
            cell: (r) => <span className="font-mono text-accent">{fmt(r.specialDiscount)}</span>,
          },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => <StatusBadge status={r.status} />,
          },
          {
            key: 'remarks',
            header: 'Remarks',
            cell: (r) => <span className="text-[var(--text-muted)]">{r.remarks ?? '—'}</span>,
          },
          {
            key: 'actions',
            header: '',
            width: '110px',
            cell: (r) => (
              <div className="flex gap-1.5">
                <Button variant="glass" size="sm" onClick={() => openEdit(r)}>Edit</Button>
                <Button
                  variant="glass"
                  size="sm"
                  onClick={async () => { if (await confirmDlg('Delete this entry?')) deleteMut.mutate(r.id); }}
                  title="Delete entry"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ),
          },
        ]}
      />

      {/* Add / edit form — shared themed Modal */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        size="sm"
        title={editingId ? 'Edit Price Entry' : 'New Price Entry'}
        footer={
          <>
            <Button variant="glass" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button
              type="submit"
              form="price-entry-form"
              variant="primary"
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form
          id="price-entry-form"
          onSubmit={(e) => { e.preventDefault(); saveMut.mutate(form); }}
          className="grid grid-cols-1 gap-3"
        >
          <FormGroup label="Customer Type" required>
            <Select
              required
              disabled={!!editingId}
              value={form.priceList}
              onChange={(e) => setForm((f) => ({ ...f, priceList: e.target.value }))}
            >
              {CUSTOMER_TYPES.map((ct) => (
                <option key={ct} value={ct}>{ct}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup label="Discount %" required>
            <Input
              type="number" min="0" max="100" step="0.01"
              value={form.discount ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value ? parseFloat(e.target.value) : null }))}
              placeholder="e.g. 10.00"
              className="font-mono"
            />
          </FormGroup>

          <FormGroup label="Special Discount %" hint="Applied on the post-discount value">
            <Input
              type="number" min="0" max="100" step="0.01"
              value={form.specialDiscount ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, specialDiscount: e.target.value ? parseFloat(e.target.value) : null }))}
              placeholder="e.g. 5.00"
              className="font-mono"
            />
          </FormGroup>

          <FormGroup label="Status">
            <Select
              value={form.status ?? 'Active'}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Select>
          </FormGroup>

          <FormGroup label="Remarks">
            <Textarea
              rows={3}
              maxLength={400}
              value={form.remarks ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value || null }))}
            />
          </FormGroup>
        </form>
      </Modal>
    </div>
  );
}
